/**
 * Fábrica del reducer de fórmulas — precios y costos (H7, Fase 6).
 * ============================================================================
 *
 * ─── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Precios y costos son **la misma pantalla con otro `type`**: mismo endpoint,
 * mismos filtros, mismas acciones. Hasta la Fase 6 eso se resolvió de la forma
 * más directa posible —**compartiendo el trozo del store**— y salió caro:
 *
 * - Buscar en precios **arrastraba la búsqueda** a costos.
 * - La respuesta que llegara **la última** ganaba, así que una pantalla podía
 *   acabar pintando las fórmulas del otro tipo.
 * - Cualquier acción que rehiciera `filters` **relanzaba las dos** peticiones.
 *
 * Separarlos copiando 159 líneas habría resuelto eso y creado otro problema:
 * dos reducers gemelos que se separan en cuanto alguien toque uno solo. De ahí
 * la fábrica: **una implementación, dos trozos de estado independientes**.
 *
 * @param {object} cfg
 * @param {object} cfg.tipos    Los tipos de acción de este trozo.
 * @param {string} cfg.rutaBase Ruta a la que redirigir tras crear o editar.
 */
export const crearReducerDeFormulas = ({ tipos, rutaBase }) => {
  const INICIAL = {
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isDeleting: false,
    activeAction: null,
    needRefresh: false,
    selected: false,
    data: undefined,
    errors: [],
    filters: {
      search: '',
      page: 1,
      type: ''
    }
  }

  return function reducer(state = INICIAL, action) {
    switch (action.type) {
      case tipos.LISTA_PETICION:
        return { ...state, isFetching: true, needRefresh: false }

      case tipos.LISTA_EXITO:
        return { ...state, isFetching: false, data: action.data }

      // Antes no estaba manejado (B12): `isFetching` se quedaba en `true` para
      // siempre y el error no llegaba a ninguna parte. `data` se conserva —si
      // falla una recarga, seguir mostrando la lista anterior es mejor que
      // vaciar la pantalla.
      case tipos.LISTA_FALLO:
        return {
          ...state,
          isFetching: false,
          errors: action.errors || action.data
        }

      case tipos.ACTUALIZAR_FILTROS:
        return {
          ...state,
          filters: { ...state.filters, ...action.data },
          needRefresh: true
        }

      case tipos.SELECCIONAR:
        return { ...state, selected: action.data }

      case tipos.MOSTRAR_MENSAJE:
        return { ...state, message: true }

      case tipos.OCULTAR_MENSAJE:
        return { ...state, message: false }

      case tipos.BORRADO_PETICION:
        return { ...state, isDeleting: true }

      case tipos.BORRADO_EXITO:
        return {
          ...state,
          isDeleting: false,
          activeAction: null,
          needRefresh: true
        }

      case tipos.BORRADO_FALLO:
        return { ...state, isDeleting: false }

      case tipos.DETALLE_PETICION:
        return { ...state, isFetching: true }

      case tipos.DETALLE_EXITO:
        return { ...state, isFetching: false, dataDetail: action.data }

      case tipos.DETALLE_FALLO:
        return { ...state, isFetching: false, errors: action.data }

      case tipos.EDICION_PETICION:
        return { ...state, isUpdating: true }

      // La redirección va por `rutaBase`: antes estaba escrita a mano apuntando
      // siempre a `/configuration/prices/`, así que editar un **costo** habría
      // llevado al listado de **precios**.
      case tipos.EDICION_EXITO:
        window.location = `${rutaBase}/`
        return { ...state, isUpdating: false, needRefresh: true }

      case tipos.EDICION_FALLO:
        return { ...state, isUpdating: false }

      case tipos.CREACION_EXITO:
        window.location = `${rutaBase}/${action.data.id}`
        return { ...state, isCreating: false, formulaData: action.data }

      case tipos.CREACION_FALLO:
        return { ...state, isCreating: false, error: action.errors }

      case tipos.REINICIAR:
        return { ...state, dataDetail: {}, data: undefined }

      default:
        return state
    }
  }
}
